/**
 * Root layout, lifted from the legacy `_app.tsx` so route components
 * render inside the same provider tree the Next app used:
 *   QueryClientProvider → ThemeProvider → MaintenanceGate →
 *   { AdminLayout | MainLayout }.
 *
 * Layout differences between admin and non-admin routes are preserved:
 *   - Admin: header-less, single column, no sidebar/mini-player/ads.
 *   - Non-admin: Sidebar + MobileTopNav + MobileBottomNav + Footer +
 *     FloatingMiniPlayer + AdManager + ChangelogDialog + UserMediaSync.
 *
 * The 4 "ssr: false" dynamic imports from the legacy code now go
 * through `@/lib/dynamic` which uses React.lazy + a
 * client-only mount guard. Behavior is the same: nothing renders
 * during SSR, the shell paints first, dynamic islands hydrate after.
 */
import dynamic from '@/lib/dynamic';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AnimatePresence } from 'motion/react';
import { ThemeProvider } from 'next-themes';
import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { toast } from 'sonner';

import { AdminGate } from '@/components/admin/admin-gate';
import { AdminLayout as AdminShell } from '@/components/admin/admin-layout';
import MaintenanceScreen from '@/components/general/maintenance-screen';
import { ErrorBoundary } from '@/components/layout/error-boundary';
import { Footer } from '@/components/layout/footer';
import { LenisProvider } from '@/components/layout/lenis-provider';
import MobileBottomNav from '@/components/layout/mobile-bottom-nav';
import MobileTopNav from '@/components/layout/mobile-top-nav';
import { RouteLoader } from '@/components/layout/route-loader';
import { RouteSkeleton } from '@/components/layout/route-skeleton';
import { Sidebar } from '@/components/layout/sidebar';
import { Toaster } from '@/components/ui/sonner';
import { useAppSettings } from '@/hooks/use-app-settings';
import {
  ANILIST_RELINK_REQUIRED_EVENT,
  authClient,
  useSession,
} from '@/lib/auth-client';
import { applyStoredTheme, setRuntimeThemePresets } from '@/lib/theme-utils';

const ChangelogDialog = dynamic(
  () =>
    import('@/components/general/changelog-dialog').then(
      (module) => module.ChangelogDialog
    ),
  { ssr: false }
);

const UserMediaSync = dynamic(
  () => import('@/components/general/user-media-sync'),
  { ssr: false }
);

// Music player chrome is direct-imported (not dynamic) so it always
// mounts with App. Dynamic + ssr:false would land each piece in a
// Lazy chunk that suspends *without* a Suspense boundary above (they
// live as siblings of the Outlet's Suspense, not inside it). That made
// playback flaky — the engine sometimes never registered with the
// controller because its chunk hadn't loaded by the time the user
// clicked play.
import GlobalMusicEngine from '@/components/music/global-music-engine';
import ImmersiveMusicPlayer from '@/components/music/immersive-music-player';
import MusicDrawer from '@/components/music/music-drawer';
import MusicPlayerBar from '@/components/music/music-player-bar';
import PlayActionModal from '@/components/music/play-action-modal';

const AdManager = dynamic(
  () =>
    import('@/components/ads/ad-manager').then((module) => module.AdManager),
  { ssr: false }
);

function useAniListRelinkReminder(
  session: ReturnType<typeof useSession>
): void {
  useEffect(() => {
    const userId = session?.data?.user?.id;
    if (session.isPending || !userId) {
      return () => {};
    }

    const reminderKey = `anilist-relink-reminder:${userId}`;
    const showReminder = (message?: string): void => {
      if (typeof window !== 'undefined') {
        const alreadyShown = window.sessionStorage.getItem(reminderKey);
        if (alreadyShown) return;
        window.sessionStorage.setItem(reminderKey, 'shown');
      }
      toast.warning(
        message || 'Relink AniList to keep AniList features working.',
        {
          duration: 12_000,
          action: {
            label: 'Relink',
            onClick: () => authClient.relinkAnilist(window.location.href),
          },
        }
      );
    };

    const handleRelinkRequired = (event: Event): void => {
      const { detail } = event as CustomEvent<{ message?: string }>;
      showReminder(
        detail?.message ||
          'AniList rejected the request. Relink AniList to keep syncing working.'
      );
    };

    window.addEventListener(
      ANILIST_RELINK_REQUIRED_EVENT,
      handleRelinkRequired
    );
    return () => {
      window.removeEventListener(
        ANILIST_RELINK_REQUIRED_EVENT,
        handleRelinkRequired
      );
    };
  }, [session.data?.user?.id, session.isPending]);
}

function useThemeBootHydration(): void {
  useEffect(() => {
    let cancelled = false;

    // Apply the user's stored theme IMMEDIATELY from localStorage so the
    // page paints with their preferred colors without waiting for the
    // /api/themes fetch.
    applyStoredTheme();

    const hydrateThemePresets = async (): Promise<void> => {
      try {
        const response = await fetch('/api/themes');
        if (!response.ok) return;
        const payload = (await response.json()) as {
          themes?: Array<{
            name: string;
            type: string;
            variables: Record<string, string>;
            dark?: Record<string, string> | null;
            isActive?: boolean;
            isDefault?: boolean;
          }>;
        };
        const runtimePresets = Array.isArray(payload.themes)
          ? payload.themes
              .filter((theme) => theme.isActive !== false)
              .map((theme) => ({
                name: theme.name,
                type: theme.type,
                variables: theme.variables,
                dark: theme.dark ?? undefined,
                isDefault: theme.isDefault === true,
              }))
          : [];

        if (cancelled) return;
        if (runtimePresets.length > 0) {
          setRuntimeThemePresets(runtimePresets);
          // Re-apply the stored theme now that the runtime cache is
          // populated. Covers two cases: (1) the user's selected theme
          // wasn't in the cache yet on first visit and fell back to
          // the built-in Default; (2) the DB-marked `isDefault` theme
          // changed since the last load. Without this the fallback
          // would stay locked in until the next full reload.
          applyStoredTheme();
        }
      } catch {
        // ignore — stored theme is already applied
      }
    };

    Promise.resolve().then(hydrateThemePresets);
    return () => {
      cancelled = true;
    };
  }, []);
}

// Drives the modern overlay loader's visibility off pathname changes
// rather than Suspense state. In dev, lazy chunks are often cached or
// resolve so fast that Suspense never fires — meaning a Suspense-only
// loader is effectively invisible. Reacting to `location.pathname`
// guarantees the loader shows on *every* navigation, and the minimum
// display window (500ms) keeps cached navigations from flashing it
// for a frame. The initial mount is skipped (no loader on the very
// first paint — SSR has already painted real content there).
function useRouteTransitionLoader(pathname: string): boolean {
  const [visible, setVisible] = useState(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return undefined;
    }
    setVisible(true);
    const timeout = window.setTimeout(() => setVisible(false), 500);
    return () => window.clearTimeout(timeout);
  }, [pathname]);

  return visible;
}

function MaintenanceGate({
  pathname,
  children,
}: {
  pathname: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const { site } = useAppSettings();
  const isAdmin = pathname.startsWith('/admin');
  const isBetaEnv =
    typeof window !== 'undefined' &&
    window.location.hostname === 'beta.1anime.app';
  const shouldBypassMaintenance =
    isAdmin ||
    import.meta.env.DEV ||
    (isBetaEnv && site.betaBypass);

  if (site.loaded && site.enabled && !shouldBypassMaintenance) {
    return (
      <MaintenanceScreen
        reason={site.reason}
        startAt={site.startAt}
        etaAt={site.etaAt}
      />
    );
  }
  return <div>{children}</div>;
}

function AdminLayout(): React.JSX.Element {
  const location = useLocation();
  const loaderVisible = useRouteTransitionLoader(location.pathname);

  // Gate the entire admin shell behind a session/role check — the gate
  // owns the "verifying access" loading UI, the access-denied screen,
  // and the sign-in prompt so the actual admin chrome only renders for
  // confirmed admins / developers.
  return (
    <>
      <Toaster />
      <AdminGate>
        <AdminShell>
          <Suspense fallback={<RouteSkeleton />}>
            <Outlet />
          </Suspense>
        </AdminShell>
        <AnimatePresence>{loaderVisible && <RouteLoader />}</AnimatePresence>
      </AdminGate>
    </>
  );
}

function MainLayout(): React.JSX.Element {
  const location = useLocation();
  const session = useSession();
  const sessionUserRole = session.data?.user?.role;
  useAniListRelinkReminder(session);
  const loaderVisible = useRouteTransitionLoader(location.pathname);

  const is404 = false; // RR7 handles 404 via a separate route node
  const isWatchPage = location.pathname.startsWith('/watch/');
  const isCoverPage =
    location.pathname === '/discover' ||
    location.pathname.startsWith('/anime/') ||
    location.pathname.startsWith('/manga/');
  const reservesTopNav = !(isCoverPage || is404);
  const topPaddingClasses = reservesTopNav
    ? 'pt-[calc(3.5rem+env(safe-area-inset-top))] md:pt-[env(safe-area-inset-top)]'
    : '';
  const noFooterPaths: string[] = ['/settings'];
  const noMobileBottomNavPaths = [''];
  const shouldShowFooter = !noFooterPaths.includes(location.pathname);

  // Body-style cleanup on route change. The legacy code mixed this in
  // with a fullscreen `<Loading />` overlay flashed for 100ms on every
  // nav — we ditched the overlay (each route now suspends into its own
  // skeleton via the `<Suspense>` boundary around `<Outlet />` below),
  // but dialogs can still leave behind `body.overflow:hidden` etc., so
  // the cleanup half stays.
  useEffect(() => {
    return () => {
      try {
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('top');
        document.body.style.removeProperty('width');
        document.documentElement.style.removeProperty('overflow');
      } catch {
        // ignore
      }
    };
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <LenisProvider />
      <Toaster />
      <AdManager
        isWatchPage={isWatchPage}
        isSessionPending={session.isPending}
        userRole={sessionUserRole}
      />
      <ChangelogDialog />
      <UserMediaSync />
      <div className="flex flex-1">
        {!is404 && <Sidebar />}
        <MobileTopNav />
        <main
          className={`mb-[56px] ml-0 flex flex-1 flex-col transition-[margin-left,max-width] duration-300 ease-in-out md:mb-0 ${
            is404 ? '' : 'md:ml-[72px] md:max-w-[calc(100vw-72px)]'
          } ${topPaddingClasses}`}
        >
          <div className="flex-grow">
            <ErrorBoundary>
              {/* Suspense fallback paints the per-route skeleton while
                * the next lazy chunk is in flight. RouteSkeleton reads
                * `useLocation()` so it picks the destination's shape
                * (DiscoverSkeleton, WatchSkeleton, …), keeping the
                * sidebar + topnav chrome stable across navigation. */}
              <Suspense fallback={<RouteSkeleton />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </div>
          {shouldShowFooter && <Footer />}
        </main>
      </div>
      <GlobalMusicEngine />
      <MusicPlayerBar />
      <MusicDrawer />
      <ImmersiveMusicPlayer />
      <PlayActionModal />
      {!noMobileBottomNavPaths.includes(location.pathname) && (
        <MobileBottomNav />
      )}
      {/* Always-on overlay loader, driven by pathname change rather
        * than Suspense. Guarantees a visible loading affordance even
        * when chunks are warm-cached. */}
      <AnimatePresence>{loaderVisible && <RouteLoader />}</AnimatePresence>
    </div>
  );
}

function RootInner(): React.JSX.Element {
  useThemeBootHydration();
  const location = useLocation();

  if (location.pathname.startsWith('/admin')) {
    return <AdminLayout />;
  }

  return (
    <MaintenanceGate pathname={location.pathname}>
      <MainLayout />
    </MaintenanceGate>
  );
}

let queryClient: QueryClient | null = null;
function getQueryClient(): QueryClient {
  if (queryClient === null) queryClient = new QueryClient();
  return queryClient;
}

export default function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={getQueryClient()}>
      <ThemeProvider attribute="class" defaultTheme="dark">
        <RootInner />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
